import { Workbook } from "exceljs";
import {
	DisbursedIncentiveRow,
	DisbursedIncentivesFilters,
} from "../../services/reports.service";
import { applyHeaderStyle, currencyFmt, filenameSlug } from "./workbook-styles";
import { BuiltWorkbook } from "./build-workbook";

/** AC1 + C15537: exactly one sheet, and it is named this. */
export const DISBURSEMENTS_SHEET = "Disbursements";

export interface DisbursedIncentivesWorkbookInput
	extends DisbursedIncentivesFilters {
	rows: DisbursedIncentiveRow[];
	/** Whole-set total from the report — never re-summed from `rows`. */
	totalDisbursed: number;
	/** The heading the SCREEN rendered, e.g. "Member Full Name" — AC4. */
	beneficiaryHeading: string;
	/** The symbol the SCREEN rendered — AC4. */
	currencySymbol: string;
	/** Selected program names, for the filename — AC2 / C15809. */
	programNames?: string[];
	dateFormat?: string;
}

/**
 * AC2 / C15809 — a multi-program export must not be labelled with one program's
 * name, and a single-program export should still say which program it was.
 */
export const buildDisbursedFilename = (
	programNames: string[] | undefined,
	extension: string,
): string => {
	const names = (programNames ?? []).filter(Boolean);
	const stem =
		names.length === 0
			? "disbursed-incentives"
			: names.length === 1
				? `disbursed-incentives-${filenameSlug(names[0])}`
				: `disbursed-incentives-${names.length}-programs`;
	return `${stem}.${extension}`;
};

/**
 * SIMS-8155 — the Disbursements export, built ENTIRELY IN THE BROWSER.
 *
 * Client-side for the same reason Visit Detail is: the backend export path
 * base64-encoded the workbook through API Gateway and it arrived undecoded, so
 * Excel rejected the file.
 *
 * **One sheet only.** DR-9 describes a two-sheet workbook, but the BRD splits it
 * — Sheet 2 ("Visits included") belongs to Epic SIMS-8177. Naming this sheet
 * "Disbursements" now lets that Epic append without restructuring, and C15537
 * asserts Sheet 2 is absent here.
 *
 * **AC3 and AC4 hold by data path, not by re-implementation.** The rows arrive
 * from the same endpoint the screen used, so a withheld name already reads as an
 * em dash — this builder never sees the name and so cannot leak it. The term and
 * symbol are likewise the values the screen rendered, passed through. One value
 * read twice cannot diverge; two implementations kept in step eventually do.
 */
export const buildDisbursedIncentivesWorkbook = async (
	input: DisbursedIncentivesWorkbookInput,
): Promise<BuiltWorkbook> => {
	const workbook = new Workbook();
	const sheet = workbook.addWorksheet(DISBURSEMENTS_SHEET);

	const money = currencyFmt(input.currencySymbol);

	sheet.columns = [
		{ header: "Program", key: "programName", width: 28 },
		{ header: "Transaction Date", key: "transactionDate", width: 18 },
		{ header: "CIN", key: "cin", width: 16 },
		// AC4: the heading the screen showed, not a fixed word.
		{ header: input.beneficiaryHeading, key: "beneficiaryName", width: 28 },
		{ header: "Vendor Card", key: "vendorCard", width: 24 },
		{ header: "Delivery", key: "delivery", width: 16 },
		{ header: "Amount", key: "amount", width: 16, style: { numFmt: money } },
	];
	applyHeaderStyle(sheet.getRow(1));

	for (const row of input.rows) {
		sheet.addRow({
			programName: row.programName,
			// Date-only, so the file does not imply a precision the report does not
			// show. ISO in the cell keeps Excel's own sorting correct.
			transactionDate: row.transactionDate
				? row.transactionDate.slice(0, 10)
				: "",
			cin: row.cin,
			// AC3: already an em dash when the account withholds names.
			beneficiaryName: row.beneficiaryName,
			vendorCard: row.vendorCard,
			delivery: row.delivery,
			amount: row.amount,
		});
	}

	// AC1: the grand total, taken from the report rather than re-summed from the
	// exported rows — those are capped, and a total that disagreed with the screen
	// is exactly the discrepancy this report exists to avoid.
	const totalRow = sheet.addRow({
		programName: "Total disbursed",
		amount: input.totalDisbursed,
	});
	totalRow.font = { bold: true };
	totalRow.getCell("amount").numFmt = money;

	const buffer = await workbook.xlsx.writeBuffer();
	return {
		blob: new Blob([buffer], {
			type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
		}),
		filename: buildDisbursedFilename(input.programNames, "xlsx"),
	};
};
